import { z } from "zod"
import type { TClientApiErrorCode } from "./api-error-codes"
import { API_ERROR_CODES, AUTHORIZER_ERROR_CODES, UNKNOWN_API_ERROR_CODE } from "./api-error-codes"

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  TRANSPORT ERROR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ── Transport metadata (diagnostics only — app logic must not branch on these) ──

const transportMetaSchema = z.object({
  adapter: z.enum(["axios", "fetch"]),
  adapterCode: z.string().optional(),
})
/** Adapter-level diagnostic metadata attached to transport errors. App logic must not branch on these fields. */
type _TTransportMeta = Readonly<z.infer<typeof transportMetaSchema>>

// ------------------------------------------------------------------------------------------------
const timeoutErrorSchema = z.object({
  type: z.literal("timeout"),
  message: z.string(),
  meta: transportMetaSchema,
})
/** The request timed out before a response was received (e.g. ECONNABORTED, ETIMEDOUT). */
type _TTimeoutError = Readonly<z.infer<typeof timeoutErrorSchema>>

/** Constructs a timeout member of {@link me.TTransport}. */
function _createTimeout(message: string, meta: _TTransportMeta): _TTimeoutError {
  const e = {
    type: "timeout",
    message,
    meta,
  } satisfies _TTimeoutError

  return e
}
// ------------------------------------------------------------------------------------------------

// ------------------------------------------------------------------------------------------------
const noResponseErrorSchema = z.object({
  type: z.literal("no_response"),
  message: z.string(),
  meta: transportMetaSchema,
})
/** Request was sent but no HTTP response was received (e.g. DNS failure, connection refused, CORS block). */
type _TNoResponseError = Readonly<z.infer<typeof noResponseErrorSchema>>

/** Constructs a no_response member of {@link me.TTransport}. */
function _createNoResponse(message: string, meta: _TTransportMeta): _TNoResponseError {
  const e = {
    type: "no_response",
    message,
    meta,
  } satisfies _TNoResponseError

  return e
}
// ------------------------------------------------------------------------------------------------

// ------------------------------------------------------------------------------------------------
const canceledErrorSchema = z.object({
  type: z.literal("canceled"),
  message: z.string(),
  meta: transportMetaSchema,
})
/** The request was canceled (e.g. navigation interruption, AbortController, component unmount). */
type _TCanceledError = Readonly<z.infer<typeof canceledErrorSchema>>

/** Constructs a canceled member of {@link me.TTransport}. */
function _createCanceled(message: string, meta: _TTransportMeta): _TCanceledError {
  const e = {
    type: "canceled",
    message,
    meta,
  } satisfies _TCanceledError

  return e
}
// ------------------------------------------------------------------------------------------------

// ------------------------------------------------------------------------------------------------
const unexpectedErrorSchema = z.object({
  type: z.literal("unexpected"),
  message: z.string(),
  cause: z.unknown(),
  meta: transportMetaSchema,
})
/** A catch-all for transport errors outside recognized categories; preserves the original `cause`. */
type _TUnexpectedTransportError = Readonly<z.infer<typeof unexpectedErrorSchema>>

/** Constructs an unexpected member of {@link me.TTransport}. */
function _createUnexpected(
  message: string,
  cause: unknown,
  meta: _TTransportMeta,
): _TUnexpectedTransportError {
  const e = {
    type: "unexpected",
    message,
    cause,
    meta,
  } satisfies _TUnexpectedTransportError

  return e
}
// ------------------------------------------------------------------------------------------------

/** Union of all transport errors, independent of API business logic. */
type _TTransport = _TCanceledError | _TNoResponseError | _TTimeoutError | _TUnexpectedTransportError

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  PARSE ERROR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const parseErrorSchema = z.object({
  type: z.literal("parse"),
  message: z.string(),
  issues: z.array(z.custom<z.core.$ZodIssue>()),
})
/** A successful HTTP response was received but its body failed Zod schema validation. */
type _TParse = Readonly<z.infer<typeof parseErrorSchema>>

/** Constructs a parse member of {@link me.TParse} from a Zod validation failure. */
function _createParse(zodError: z.ZodError): _TParse {
  const e = {
    type: "parse",
    message: `Failed to parse success response: ${zodError.message}`,
    issues: zodError.issues,
  } satisfies _TParse

  return e
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  API ERROR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const apiErrorSchema = z.object({
  type: z.literal("api"),
  message: z.string().optional(),
  code: z.union([
    z.enum(API_ERROR_CODES),
    z.enum(AUTHORIZER_ERROR_CODES),
    z.literal(UNKNOWN_API_ERROR_CODE),
  ]),
  status: z.number(),
})
// Generic because each endpoint narrows to its own subset of codes.
// Intentionally not inferred from `apiErrorSchema`; `z.infer` would force
// a concrete `code: TClientApiErrorCode`.
type _TApi<TCode extends TClientApiErrorCode> = {
  readonly type: "api"
  readonly code: TCode
  readonly status: number
  readonly message: string | undefined
}

/** Constructs a {@link me.TApi} with a typed error code and HTTP status. */
function _createApi<TCode extends TClientApiErrorCode>(
  code: TCode,
  status: number,
  message?: string,
): _TApi<TCode> {
  const e = {
    type: "api",
    code,
    status,
    message,
  } satisfies _TApi<TCode>

  return e
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  COMBINED ERROR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Composed from the individual error object schemas above. Used to parse the error body.
const _combinedSchema = z.discriminatedUnion("type", [
  timeoutErrorSchema,
  noResponseErrorSchema,
  canceledErrorSchema,
  unexpectedErrorSchema,
  parseErrorSchema,
  apiErrorSchema,
])
// Widest parsed shape from `combinedSchema`; kept in sync with the parser.
type _TCombinedInferred = z.infer<typeof _combinedSchema>

// Full endpoint error union: transport failures + parse failures + endpoint-specific API errors.
type _TCombined<TCode extends TClientApiErrorCode> = _TTransport | _TParse | _TApi<TCode>

type _TNonCanceled<TCode extends TClientApiErrorCode> = Exclude<
  me.TCombined<TCode>,
  { type: "canceled" }
>

function _serialize<TCode extends TClientApiErrorCode>(error: _TCombined<TCode>): string {
  return JSON.stringify(error)
}

// Returns `unknown` on purpose: the JSON may come from any thrown response,
// so callers must validate with `me.combinedSchema` before trusting the shape.
function _deserialize(json: string): unknown {
  return JSON.parse(json)
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  EXPORTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Runtime helpers for constructing and parsing Maul client errors.
 *
 * Use `schema` to parse unknown error payloads and `create*` constructors to
 * build normalized error objects at request/endpoint boundaries.
 *
 * Declaration-merged with `namespace me`, so runtime helpers and
 * type aliases share the same `me.*` access path.
 */
export const me = {
  combinedSchema: _combinedSchema,
  createApi: _createApi,
  createTimeout: _createTimeout,
  createNoResponse: _createNoResponse,
  createCanceled: _createCanceled,
  createParse: _createParse,
  createUnexpected: _createUnexpected,
  serialize: _serialize,
  deserialize: _deserialize,
} as const

/**
 * Public type surface for Maul client errors.
 *
 * Use these aliases in endpoint modules and shared error helpers.
 *
 * Declaration-merged with the runtime `me` value, so runtime helpers and
 * type aliases share the same `me.*` access path.
 */
export namespace me {
  /** Union of all transport errors, independent of API business logic. */
  export type TTransport = _TTransport

  /** Successful response received, but response payload failed schema parsing. */
  export type TParse = _TParse

  /**
   * A domain-level API error with a typed `code`. Generic because each
   * endpoint narrows to its own subset of codes.
   *
   * Intentionally **not** inferred from `apiErrorSchema`. `z.infer` always
   * yields a concrete type (`code: TClientApiErrorCode`), but we need `code` to
   * be generic so each endpoint can narrow to its own subset of codes.
   */
  export type TApi<TCode extends TClientApiErrorCode> = _TApi<TCode>
  /**
   * The widest Maul error type — conceptually `me.TError<TClientApiErrorCode>`.
   * Inferred from `combinedSchema` so it stays in sync with the Zod parser;
   * used at parsing boundaries where `result.data` flows out of
   * `combinedSchema.safeParse()` and we want the return type to match exactly.
   */
  export type TCombinedInferred = _TCombinedInferred
  /**
   * The full error type for any Maul API endpoint. Combines transport-level failures,
   * parse failures, and endpoint-specific API error codes (plus `"UnknownError"` for unrecognized codes).
   */
  export type TCombined<TCode extends TClientApiErrorCode> = _TCombined<TCode>
  /**
   * The full error type for any Maul API endpoint, excluding canceled errors.
   */
  export type TNonCanceled<TCode extends TClientApiErrorCode> = _TNonCanceled<TCode>
}
